/**
 * THE WEIGHTED TREE — one indented table of rows that each carry a weight in integer tenths, with
 * a live running total under every sibling set and the two helpers that fix one.
 *
 * ── WHY THIS IS ONE COMPONENT AND NOT TWO SCREENS' WORTH OF THE SAME CODE ──────────────
 *
 * Two things in this module are the same object: a CAPABILITY MODEL's tree (§6.9-§6.17) and a
 * CAPABILITY TEMPLATE's lines (§6.2-§6.7). That is not a resemblance somebody noticed — it is the
 * schema's own statement. `0094` created `capability_template_line` with *"Arbitrary depth, same
 * tree shape as capability. A top-level line is a functional area."*, the contract specifies the
 * template's weight endpoint as *"identical in shape and semantics"* to the model's (§6.7), and
 * the server honours that by running ONE validator (`weights/write.ts`) and ONE balance report
 * (`weights/balance.ts`) over both tables.
 *
 * So the 100% rule already exists exactly once on the server and exactly once in
 * `@platform/erp-scoring`. A second transcription of the SCREEN around it would be the third copy,
 * and D-009 names the consequence: *"a rule implemented twice is a rule that will eventually
 * disagree with itself."* Here the disagreement would be visible rather than arithmetic — one
 * screen's remainder going to a different sibling than the other's, or one of them keeping a
 * behaviour the other had fixed.
 *
 * **This is NOT the refactor CLAUDE.md §1.2.1 forbids.** That rule forbids collapsing several
 * SOURCE screens into one parameterised component during a PORT, because the source's screens are
 * the specification. This module has no source app (DECISIONS.md, the catalogue entry: *"NEW WORK,
 * not a port — there is no source app for it"*), and the markup below is the clone of APR's
 * Framework Builder that `CapabilityModelEditor.tsx` already made and already cites. Nothing about
 * that clone changes here: it MOVES, element for element and class for class, out of a screen file
 * and into the component both screens render. Verified by selector set rather than by eye — see
 * `WeightedTree.css`'s header.
 *
 * ── WHAT IS THE SAME ON BOTH SCREENS, AND IS THEREFORE IN HERE ─────────────────────────
 *
 * The indented tree column with its `▸`/`▾` toggles; inline editable names saved on blur; the
 * numeric weight cell in tenths; the live sibling total with §6.1's two sentences — *"x% left to
 * allocate"* and *"x% over"* — from the one formatter both the client and the server run; and the
 * ONE helper, **Distribute evenly**, which is deliberately not an endpoint (§3.2: *"a server
 * route for them would be a second place the remainder rule lives"*).
 *
 * THERE WERE TWO. **Normalize proportionally** was removed on the owner's instruction,
 * 2026-10-07: *"what is the difference between 'Distribute evenly' and 'Normalize
 * proportionally' - they alwat produce the same result. Leave only 'Distribute evenly'"*.
 *
 * He was right about what he was seeing, and the reason is worth keeping. Distribute evenly gives
 * each unlocked sibling 100/n; normalize scales the existing weights so they sum to 100. The two
 * agree whenever those weights are all EQUAL — and all zero is a special case of equal, which is
 * the state every freshly built tree is in, because §6.1 starts each new row at 0.0% rather than
 * taking anything from its siblings. So on the trees he was working in the two were identical by
 * construction, and the second button was a choice with no consequence.
 *
 * `normalizeProportionally` is STILL EXPORTED by `@platform/erp-scoring` and still covered by its
 * tests. Only this screen's button is gone — the arithmetic was not deleted, so bringing the
 * control back is re-adding a button, not re-deriving a rule.
 *
 * ── WHAT DIFFERS, AND IS THEREFORE A PROP ─────────────────────────────────────────────
 *
 *  1. **THE LOCK.** A capability has `weight_locked` and a template line does not — `0094` gave
 *     the template no such column, and `capabilityTemplates.ts` records the consequence: *"there
 *     is nothing on a template line to lock and no `weight_locked` refusal here."* So the padlock
 *     renders only when `onToggleLock` is given. The arithmetic needs no branch either way: with
 *     every row unlocked, `weightCapacity` and `distributeEvenly` behave exactly as they do on a
 *     model whose weights are all unlocked.
 *  2. **THE EFFECTIVE COLUMN.** The model's read computes `effective_weight_tenths` down the tree
 *     and §3.3 is explicit that the screen renders it and never recomputes it. The template's read
 *     computes nothing of the kind, so `showEffective` is false there and the column is absent —
 *     rather than this file deriving a figure the server did not send.
 *  3. **THE NOUNS.** "Capability" / "capabilities" against "Line" / "lines", and what the root
 *     sibling set is called on its total row. Copy is a prop because copy is the one thing that
 *     must not be guessed at by a shared component.
 *  4. **THE PER-ROW ADD CONTROL.** Present on a template's lines, ABSENT on a capability model —
 *     the owner moved that screen's add control to the model level (2026-10-05). `onAddChild` is
 *     therefore optional and the button renders only where a screen asks for it, so one screen can
 *     lose the control without the other losing it too. See the prop.
 *
 * ── WHAT IT DOES NOT OWN ──────────────────────────────────────────────────────────────
 *
 * It holds exactly one piece of state: which rows are COLLAPSED, which is presentation and is
 * nobody else's business. Everything else belongs to the screen — the in-progress `weights`, the
 * cell being typed in, every API call, every toast, and the confirmation before a delete. A
 * component that wrote to the server would have to know which of two endpoint families it was
 * talking to, which is the coupling this split exists to avoid.
 */
import { useMemo, useState } from 'react';
/**
 * THE ARITHMETIC IS `@platform/erp-scoring`'s, through this module's `weights.ts` re-export of it.
 * The server runs the same functions, which is why the sentence beside a total while somebody
 * types is byte-identical to the sentence in the 409 they get if they try to use it anyway.
 */
import {
  checkBalance,
  distributeEvenly,
  ONE_HUNDRED_PCT_TENTHS,
  tenthsToText,
  weightCapacity,
  WeightHelperError,
  type BalanceResult,
  type WeightChange,
  type WeightSibling,
} from '../weights';
import './WeightedTree.css';

/**
 * One row of the tree, in the shape BOTH tables reduce to.
 *
 * Deliberately not `Capability | CapabilityTemplateLine`: a union would make every cell below
 * branch on which table it came from, and `parent_capability_id` versus `parent_line_id` is a
 * column name, not a difference in meaning. Each screen maps its own rows once.
 */
export interface WeightedTreeRow {
  readonly id: string;
  readonly parentId: string | null;
  readonly code: string | null;
  readonly name: string;
  /** The SERVER's weight, in tenths — what an edit is compared against before it is sent. */
  readonly weightTenths: number;
  /** Always `false` where the table has no lock column. */
  readonly weightLocked: boolean;
  readonly sortOrder: number;
  readonly depth: number;
  readonly childCount: number;
  /** The server's figure, or `null` where the server computes none. */
  readonly effectiveWeightTenths: number | null;
}

export interface WeightedTreeProps {
  /** Every row, flat, in the server's own order. */
  readonly rows: readonly WeightedTreeRow[];
  /** In-progress weights in tenths, keyed by row id — what every total on screen is derived from. */
  readonly weights: Readonly<Record<string, number>>;
  /** The cell being typed in, as raw text, so `"4."` and `""` stay expressible. */
  readonly editing: { readonly id: string; readonly text: string } | null;
  /** Whether this tree may be changed at all — role plus the baseline lock, decided by the screen. */
  readonly editable: boolean;
  /** A write is in flight: every control that would start a second one is disabled. */
  readonly busy: boolean;
  /** Heading on the first column, and the stem of the name field's label. */
  readonly itemColumnLabel: string;
  /** Singular and plural of what a row is, for the toggle's title and the delete's. */
  readonly itemNoun: { readonly one: string; readonly many: string };
  /** What the top-level sibling set is called on its total row. */
  readonly rootGroupLabel: string;
  /**
   * The label on the per-row add control — it reads as what it adds. Required when `onAddChild` is
   * given; meaningless without it, because the control does not render.
   */
  readonly addChildLabel?: string;
  readonly showEffective: boolean;
  readonly onTypeWeight: (row: WeightedTreeRow, text: string) => void;
  readonly onCommitWeight: (row: WeightedTreeRow, text: string) => void;
  readonly onRename: (row: WeightedTreeRow, name: string) => void;
  /** Omitted where the table has no lock column — the padlock then does not render. */
  readonly onToggleLock?: (row: WeightedTreeRow) => void;
  /**
   * The PER-ROW add control, which renders only when this is given.
   *
   * OPTIONAL because the owner moved the capability model's add control to the MODEL level, 2026-10-05:
   * *"+capability should be added not on the line but on the model level"*. One control opens a
   * panel whose Parent field already decides whether the new row is a functional area or a child,
   * so there is one place to look and no decision about where to click before you have decided what
   * you are creating. The capability model therefore passes neither this nor `addChildLabel`.
   *
   * THE TEMPLATE LIBRARY STILL PASSES BOTH, and that is deliberate rather than an oversight: the
   * ruling named the capability model's tree, and CLAUDE.md's additive rule is explicit that a
   * change to one thing is not permission to remove another. Making the prop optional is what lets
   * one screen lose the control without the other losing it too.
   */
  readonly onAddChild?: (row: WeightedTreeRow) => void;
  /**
   * EDIT THIS ROW — owner, 2026-10-07: *"but on each capability add also capability to change
   * description via icon change"*.
   *
   * The tree edits a NAME inline, on blur, and that is the only field it can reach: a
   * description is a paragraph and a table cell is the wrong place to type one. So the row
   * carries ✏️, the screen opens its own panel, and the tree stays a tree.
   *
   * Optional, like `onAddChild`, so a screen that has no panel to open does not grow a control
   * that does nothing.
   */
  readonly onEditRow?: (row: WeightedTreeRow) => void;
  /**
   * RE-ORDER ONE SIBLING SET — owner, 2026-10-07: *"have ability to drag and drop the those
   * children up and down - e,g, they could be re-arranged"*.
   *
   * Called with the parent whose children moved and the FULL new order of that set. The tree
   * does not write: it reports the gesture and the screen calls its own endpoint, exactly as it
   * does for weights.
   *
   * A DRAG NEVER RE-PARENTS. Dropping is permitted only onto a sibling, so the gesture can
   * reorder and nothing else — moving a capability under a different functional area is a
   * different operation with different consequences for both sets' weights, and conflating the
   * two into one drag is how somebody re-parents a row by accident.
   *
   * Optional: a screen that gives no handler grows no drag handle.
   */
  readonly onReorder?: (parentId: string | null, orderedIds: readonly string[]) => void;
  /** The screen confirms first, in its own wording, and then writes. */
  readonly onDelete: (row: WeightedTreeRow) => void;
  /** A helper's full replacement set, for the screen's own weights endpoint. */
  readonly onApplyWeights: (changes: readonly WeightChange[]) => void;
  /** A refusal the user has to read — the screen owns how it is shown. */
  readonly onError: (message: string) => void;
}

type RenderRow =
  | { kind: 'row'; row: WeightedTreeRow }
  /** A sibling set's roll-up — PEPM's Budget Plan group row (`BudgetPlan.tsx:237-238`). */
  | { kind: 'total'; parentId: string | null; parentName: string | null; depth: number };

function buildRows(rows: readonly WeightedTreeRow[], collapsed: ReadonlySet<string>): RenderRow[] {
  const byParent = new Map<string | null, WeightedTreeRow[]>();
  for (const row of rows) {
    const key = row.parentId;
    const set = byParent.get(key);
    if (set) set.push(row);
    else byParent.set(key, [row]);
  }
  /* The server already orders both payloads depth-first with parents before children (§6.2, §6.9);
     this keeps the same key so a locally edited tree cannot reorder itself between renders. */
  for (const set of byParent.values()) {
    set.sort(
      (a, b) =>
        a.sortOrder - b.sortOrder || a.name.localeCompare(b.name) || a.id.localeCompare(b.id),
    );
  }

  const out: RenderRow[] = [];
  const seen = new Set<string>();

  const walk = (parentId: string | null, depth: number, parentName: string | null) => {
    const children = byParent.get(parentId) ?? [];
    if (children.length === 0) return;
    children.forEach((row) => {
      if (seen.has(row.id)) return;
      seen.add(row.id);
      out.push({ kind: 'row', row });
      if (!collapsed.has(row.id)) walk(row.id, depth + 1, row.name);
    });
    out.push({ kind: 'total', parentId, parentName, depth });
  };

  walk(null, 0, null);
  for (const row of rows) {
    if (!seen.has(row.id)) out.push({ kind: 'row', row });
  }
  return out;
}

/** `pages/apr/components/RowControls.tsx:69-75` — the 12px padlock on `currentColor`. */
function LockIcon() {
  return (
    <svg className="fb-lock__icon" viewBox="0 0 12 12" aria-hidden="true">
      <rect x="2.4" y="5.4" width="7.2" height="5.2" rx="0.8" fill="currentColor" />
      <path
        d="M4.1 5.4 V3.9 a1.9 1.9 0 0 1 3.8 0 V5.4"
        stroke="currentColor"
        strokeWidth="1.3"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * One sibling set's running total. `total` is the package's `checkBalance` result — the same
 * function the server's own report calls — so `total.message` is §6.1's sentence rather than
 * anything this file composes.
 */
function renderBar(total: BalanceResult) {
  const pct = Math.min(100, Math.max(0, (total.totalTenths / ONE_HUNDRED_PCT_TENTHS) * 100));
  const over = total.deltaTenths > 0;
  const exact = total.balanced;
  return (
    <div className="fb-bar">
      <div className="fb-bar-track">
        <div
          className={`fb-bar-fill${over ? ' is-over' : ''}`}
          style={{ width: `${over ? 100 : pct}%` }}
        />
      </div>
      <span className={`fb-bar-pct${exact ? ' is-full' : over ? ' is-over' : ''}`}>
        {tenthsToText(total.totalTenths)}%
      </span>
      {/* §6.1's two sentences, VERBATIM, from the one formatter both sides run. */}
      <span className={`fb-bar-pct${exact ? ' is-full' : over ? ' is-over' : ''}`}>
        {exact ? 'valid' : total.message}
      </span>
    </div>
  );
}

export function WeightedTree({
  rows,
  weights,
  editing,
  editable,
  busy,
  itemColumnLabel,
  itemNoun,
  rootGroupLabel,
  addChildLabel,
  showEffective,
  onTypeWeight,
  onCommitWeight,
  onRename,
  onToggleLock,
  onAddChild,
  onEditRow,
  onReorder,
  onDelete,
  onApplyWeights,
  onError,
}: WeightedTreeProps) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());

  /*
   * ── DRAGGING ONE ROW WITHIN ITS OWN SIBLING SET ───────────────────────────────────────
   *
   * `draggingId` is the row under the cursor; `dropTargetId` is the sibling it would land on,
   * kept only so the row can be drawn with a drop line. Both are cleared on drop and on
   * dragend, including the dragend that fires when a drag is abandoned outside the table.
   */
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  const parentOf = (id: string): string | null =>
    rows.find((row) => row.id === id)?.parentId ?? null;

  /** A drop is legal only onto a DIFFERENT row in the SAME sibling set. */
  const canDropOn = (targetId: string): boolean =>
    draggingId !== null && draggingId !== targetId && parentOf(draggingId) === parentOf(targetId);

  const dropOn = (targetId: string) => {
    if (onReorder === undefined || draggingId === null || !canDropOn(targetId)) {
      setDraggingId(null);
      setDropTargetId(null);
      return;
    }
    const parentId = parentOf(draggingId);
    /* The set in the order it is DISPLAYED in, which is the order the user is looking at. */
    const siblingIds = rows.filter((row) => row.parentId === parentId).map((row) => row.id);
    const from = siblingIds.indexOf(draggingId);
    const to = siblingIds.indexOf(targetId);
    setDraggingId(null);
    setDropTargetId(null);
    if (from === -1 || to === -1 || from === to) return;
    /* Lift the row out, then put it back at the target's index — so dragging down lands it
       AFTER the target and dragging up lands it BEFORE, which is what the movement looks like. */
    const next = [...siblingIds];
    next.splice(from, 1);
    next.splice(to, 0, draggingId);
    onReorder(parentId, next);
  };

  const renderRows = useMemo(() => buildRows(rows, collapsed), [rows, collapsed]);

  const hasChildren = (id: string) => rows.some((row) => row.parentId === id);

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const siblingsOf = (parentId: string | null): WeightSibling[] =>
    rows
      .filter((row) => row.parentId === parentId)
      .map((row) => ({
        /* `WeightSibling` is named for the model's column, and the server accepts that key over
           either table (`weights/write.ts:76-84`), so nothing is translated on the way out. */
        capability_id: row.id,
        weight_tenths: weights[row.id] ?? row.weightTenths,
        weight_locked: row.weightLocked,
        sort_order: row.sortOrder,
      }));

  /**
   * §3.2's two helpers, run here and written through the screen's own weights endpoint.
   *
   * The package returns a FULL REPLACEMENT SET — one entry per sibling, locked ones carrying the
   * weight they already had — so the set literally sums to 1000 and a locked row is sent with no
   * change, which is why §6.17's `weight_locked` refusal is not triggered by it. Whether there is
   * anything to distribute is `weightCapacity`'s answer, not the length of the result: with
   * nothing unlocked the helpers return the set unchanged, by design.
   */
  const runHelper = (
    parentId: string | null,
    helper: (siblings: readonly WeightSibling[]) => readonly WeightChange[],
  ) => {
    const siblings = siblingsOf(parentId);
    if (weightCapacity(siblings).unlockedCount === 0) {
      onError('Every weight in this group is locked, so there is nothing to distribute.');
      return;
    }
    try {
      onApplyWeights(helper(siblings));
    } catch (err) {
      /* The package's own refusal, carrying the `code` the wire uses for the same condition. */
      if (err instanceof WeightHelperError) {
        onError(err.message);
        return;
      }
      throw err;
    }
  };

  return (
    <div className="fb-table-wrap">
      <table className={`fb-table${draggingId === null ? '' : ' fb-table--dragging'}`}>
        <colgroup>
          <col className="fb-col-cap" />
          <col className="fb-col-code" />
          <col className="fb-col-weight" />
          {showEffective && <col className="fb-col-effective" />}
          <col className="fb-col-actions" />
        </colgroup>
        <thead>
          <tr>
            <th>{itemColumnLabel}</th>
            <th>Code</th>
            <th style={{ textAlign: 'right' }}>Weight</th>
            {showEffective && <th style={{ textAlign: 'right' }}>Effective</th>}
            <th />
          </tr>
        </thead>
        <tbody>
          {renderRows.map((entry) => {
            if (entry.kind === 'total') {
              const siblings = siblingsOf(entry.parentId);
              /* The package's own check, over this set's weights. */
              const total = checkBalance(siblings.map((s) => s.weight_tenths));
              /* `weightCapacity` exists so a client can DISABLE a button instead of catching an
                 exception in a render path — its own words. */
              const capacity = weightCapacity(siblings);
              const nothingToDistribute = capacity.unlockedCount === 0 || capacity.refusal !== null;
              return (
                <tr className="fb-total-row" key={`total-${entry.parentId ?? 'root'}`}>
                  <td>
                    <div
                      className="fb-total-label"
                      style={{ paddingLeft: `${entry.depth * 16}px` }}
                    >
                      <span className="fb-total-scope">
                        {/*
                          THE PARENT'S NAME ALONE. Owner, 2026-10-07: *"remove children from
                          Logistic General Children context"*. The row sits directly beneath that
                          parent's own rows and carries their total, so "— children" was saying
                          what the position already says, and it made the longest label on the
                          screen out of the one that needed to be scanned fastest.
                        */}
                        {entry.parentId === null
                          ? rootGroupLabel
                          : (entry.parentName ?? itemColumnLabel)}
                      </span>
                    </div>
                  </td>
                  <td colSpan={2}>{renderBar(total)}</td>
                  {showEffective && <td />}
                  <td>
                    {editable && (
                      <div className="fb-helpers">
                        {/*
                          THE ADD CONTROL SITS ON THE SIBLING-TOTAL ROW, AHEAD OF Distribute evenly.

                          Owner, 2026-10-07: *"'+ capability' should be done on the level where the
                          100% progress is displayed before 'Distribute evenly'"*, and again after a
                          brief reversal: *"Actually, it is better to put it in the same level before
                          the button 'Distribute evenly'"*.

                          It had been on every row, so a 50-line tree carried 50 of them. The group's
                          own total row gives exactly one per sibling set by construction — the row
                          that says what the set adds up to is the row that adds to it — and it sits
                          beside the helper that redistributes what adding a row has unbalanced.

                          `parentId === null` IS SKIPPED: that set's children are the functional areas
                          themselves, and the band above the tree already carries "+ Functional area".
                        */}
                        {onAddChild && addChildLabel !== undefined && entry.parentId !== null && (
                          <button
                            type="button"
                            className="fb-link"
                            disabled={busy}
                            onClick={() => {
                              const parent = rows.find((candidate) => candidate.id === entry.parentId);
                              if (parent !== undefined) onAddChild(parent);
                            }}
                          >
                            {addChildLabel}
                          </button>
                        )}
                        <button
                          type="button"
                          className="fb-helper fb-helper--sm"
                          disabled={busy || nothingToDistribute}
                          title="Give every unlocked sibling an equal share; the rounding remainder goes to the first"
                          onClick={() => runHelper(entry.parentId, distributeEvenly)}
                        >
                          Distribute evenly
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            }

            const row = entry.row;
            const weightTenths = weights[row.id] ?? row.weightTenths;
            const nameClass =
              row.depth === 0
                ? 'fb-cap-name--area'
                : hasChildren(row.id)
                  ? 'fb-cap-name--child'
                  : 'fb-cap-name--leaf';
            const draggable = editable && onReorder !== undefined;
            /*
              THE WHOLE TABLE ANSWERS "where can this go?" WHILE A DRAG IS LIVE.
              Owner, 2026-10-07: *"I need those draggable dots more visible and I also need a
              better vizualization when dragging"*. A drop line on one row only tells you about
              that row; marking every row that is NOT a legal target tells you the shape of the
              set you are moving within, which is the thing that was invisible.
            */
            const rowClasses = [
              row.depth === 0 ? 'fb-dim-start' : '',
              draggingId === row.id ? 'fb-row-dragging' : '',
              dropTargetId === row.id && canDropOn(row.id) ? 'fb-row-drop' : '',
              draggingId !== null && draggingId !== row.id && !canDropOn(row.id)
                ? 'fb-row-nodrop'
                : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <tr
                key={row.id}
                className={rowClasses}
                onDragOver={(e) => {
                  if (!draggable || !canDropOn(row.id)) return;
                  /* Without this the browser refuses the drop and fires no `drop` event. */
                  e.preventDefault();
                  setDropTargetId(row.id);
                }}
                onDragLeave={() => setDropTargetId((prev) => (prev === row.id ? null : prev))}
                onDrop={(e) => {
                  if (!draggable) return;
                  e.preventDefault();
                  dropOn(row.id);
                }}
              >
                <td className="fb-cell-cap">
                  <div className="fb-cap-row" style={{ paddingLeft: `${row.depth * 16}px` }}>
                    {/*
                      THE HANDLE IS THE ONLY DRAGGABLE PART, deliberately. Making the whole row
                      draggable fights the inline name editor in the next cell: a drag that starts
                      on an `<input>` steals the text selection, so renaming becomes unreliable in
                      exactly the rows that are easiest to grab.
                    */}
                    {draggable && (
                      <span
                        className="fb-drag-handle"
                        draggable
                        role="button"
                        tabIndex={-1}
                        aria-label={`Re-order ${row.name}`}
                        title="Drag to re-order within this level"
                        onDragStart={(e) => {
                          setDraggingId(row.id);
                          e.dataTransfer.effectAllowed = 'move';
                          /* Firefox starts no drag at all without data on the transfer. */
                          e.dataTransfer.setData('text/plain', row.id);
                        }}
                        onDragEnd={() => {
                          setDraggingId(null);
                          setDropTargetId(null);
                        }}
                      >
                        ⠿
                      </span>
                    )}
                    {hasChildren(row.id) ? (
                      <button
                        type="button"
                        className="fb-tree-toggle"
                        aria-expanded={!collapsed.has(row.id)}
                        title={
                          collapsed.has(row.id)
                            ? `Show ${row.childCount} child ${
                                row.childCount === 1 ? itemNoun.one : itemNoun.many
                              }`
                            : 'Hide children'
                        }
                        onClick={() => toggle(row.id)}
                      >
                        {collapsed.has(row.id) ? '▸' : '▾'}
                      </button>
                    ) : (
                      <span className="fb-tree-spacer" />
                    )}
                    <input
                      /*
                       * KEYED ON THE NAME, so the cell goes back to the server's value when a
                       * rename does not happen.
                       *
                       * The field is uncontrolled — `defaultValue`, committed on blur — which means
                       * React will not put a rejected or cancelled rename back: the typed text would
                       * sit in the cell while the server still held the old name. Keying on the name
                       * remounts the input whenever the name the server reports changes, which is
                       * exactly when the cell must be re-rendered from it. It cannot interrupt
                       * typing, because `row.name` does not move while somebody types.
                       */
                      key={`${row.id}:${row.name}`}
                      className={`fb-edit fb-cap-name ${nameClass}`}
                      defaultValue={row.name}
                      disabled={!editable}
                      aria-label={`${itemColumnLabel} name`}
                      placeholder={`${itemColumnLabel} name`}
                      onBlur={(e) => onRename(row, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                      }}
                    />
                  </div>
                </td>
                <td>{row.code ?? 'N/A'}</td>
                <td>
                  <div className="fb-weight-cell">
                    <input
                      className="fb-weight-input"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      inputMode="decimal"
                      disabled={!editable || row.weightLocked}
                      aria-label={`Weight for ${row.name}`}
                      value={editing?.id === row.id ? editing.text : tenthsToText(weightTenths)}
                      onChange={(e) => onTypeWeight(row, e.target.value)}
                      onBlur={(e) => onCommitWeight(row, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                      }}
                      /* So typing in a cell never toggles the row it sits in —
                         `BudgetPlan.tsx:296`. */
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="fb-weight-unit">%</span>
                    {editable && onToggleLock && (
                      <button
                        type="button"
                        className={`fb-lock${row.weightLocked ? ' is-locked' : ''}`}
                        aria-pressed={row.weightLocked}
                        title={
                          row.weightLocked
                            ? 'Locked — the helpers will leave this weight alone'
                            : 'Lock this weight'
                        }
                        disabled={busy}
                        onClick={() => onToggleLock(row)}
                      >
                        <LockIcon />
                      </button>
                    )}
                  </div>
                </td>
                {showEffective && (
                  <td style={{ textAlign: 'right' }}>
                    <span className="fb-effective">
                      {tenthsToText(row.effectiveWeightTenths ?? 0)}%
                    </span>
                  </td>
                )}
                <td>
                  {editable && (
                    <div className="fb-helpers">
                      {/* ✏️ — the module's edit glyph, as the record lists use it. It opens the
                          screen's own panel; the tree does not know what is on it. */}
                      {onEditRow && (
                        <button
                          type="button"
                          className="fb-icon-btn"
                          title={`Edit this ${itemNoun.one}'s name, code and description`}
                          aria-label={`Edit ${row.name}`}
                          disabled={busy}
                          onClick={() => onEditRow(row)}
                        >
                          ✏️
                        </button>
                      )}
                      <button
                        type="button"
                        className="fb-icon-btn fb-danger-glyph"
                        title={`Delete this ${itemNoun.one}`}
                        aria-label={`Delete ${row.name}`}
                        disabled={busy}
                        onClick={() => onDelete(row)}
                      >
                        {/* Application Master Data's delete glyph, owner-directed: one delete
                            control across the product. */}
                        🗑️
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
